/**
 * @file keep.ts
 * @description Pins an artifact for desktop sync — "Keep on my computer".
 * Consumers: the pin control on the form and record toolbars, and the per-file
 * toggle in the attachments panel.
 *
 * `pinPath` must come from `resolveDesktopPinPath`, and that is enforced here
 * rather than trusted: this function is the only thing that mints a pin, so a
 * path the agent cannot parse must not get past it. `stopKeeping` deliberately
 * does not check, because the settings page has to be able to clean up a pin
 * whose path is already unparseable.
 *
 * Pinning a path already pinned reuses that entry and preserves its
 * `meta.created`; it never mints a second pin. An empty `files` scope is an
 * unpin spelled badly and is routed to one by `resolveDesktopPinWrite`, so
 * callers need no special case.
 *
 * **Pass a function for `scope` whenever the new scope is derived from the old
 * one** — adding or removing one file uid is exactly that. A plain value is
 * built during a render and can be a round trip stale by the time the write
 * runs, so two quick toggles would each drop the other's uid. The function form
 * is called inside the write chain, against the scope as it stands then, which
 * is what makes overlapping toggles converge.
 *
 * The current map is read off the `users/{userId}` store that `UserProvider`
 * keeps connected for the whole session. `enqueueDesktopPinWrite` guarantees
 * the previous pin write has landed before that read happens. A document that
 * has not loaded throws rather than writing a projection computed from nothing.
 */

import type { DesktopPinScope, User } from '#types';
import { createDocumentStore } from '../../store';
import { enqueueDesktopPinWrite } from './enqueueDesktopPinWrite';
import { resolveDesktopPinEntries } from './resolveDesktopPinEntries';
import { resolveDesktopPinPath } from './resolveDesktopPinPath';
import { resolveDesktopPinScope } from './resolveDesktopPinScope';
import { resolveDesktopPinWrite } from './resolveDesktopPinWrite';

/** `null` is the current scope of an artifact that is not pinned yet. */
export type DesktopPinScopeUpdater = (current: DesktopPinScope | null) => DesktopPinScope;

export const keep = async (
    userId: string,
    pinPath: string,
    scope: DesktopPinScope | DesktopPinScopeUpdater,
): Promise<void> => {
    if (resolveDesktopPinPath(pinPath) !== pinPath) {
        throw new Error(`[desktopSync] "${pinPath}" is not a pin path; fold it through resolveDesktopPinPath first.`);
    }

    return enqueueDesktopPinWrite(userId, async () => {
        const store = createDocumentStore<User>(`users/${userId}`);
        const user = store.state.data;
        if (!user) {
            throw new Error(`[desktopSync] users/${userId} has not loaded; keep() cannot write a pin without the current map.`);
        }

        const current = resolveDesktopPinEntries(user.desktopPins, pinPath)[0];
        const resolved = typeof scope === 'function'
            ? scope(current ? resolveDesktopPinScope(current.pin) : null)
            : scope;

        const update = resolveDesktopPinWrite({
            userId,
            pins: user.desktopPins,
            pinPaths: user.desktopPinPaths,
            pinPath,
            scope: resolved,
        });
        if (!update) return;

        await store.update(update, { debounce: false });
    });
};
